import React, { useCallback, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation } from 'react-router-dom';
import { resolveHelpContent } from '../../config/helpContent';
import { resolvePageTitleFromPath } from '../../config/pageTitleFromPath';
import PageHelpDrawer from './PageHelpDrawer';

export type PageHelpButtonProps = {
  /** Estado controlado del drawer (p. ej. para reservar espacio al contenido). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Botón global de ayuda contextual según la ruta actual.
 */
const PageHelpButton: React.FC<PageHelpButtonProps> = ({ open: openControlled, onOpenChange }) => {
  const { pathname } = useLocation();
  const [openInternal, setOpenInternal] = useState(false);
  const isControlled = openControlled !== undefined && onOpenChange !== undefined;
  const open = isControlled ? openControlled : openInternal;
  const setOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        onOpenChange?.(value);
      } else {
        setOpenInternal(value);
      }
    },
    [isControlled, onOpenChange]
  );
  const content = resolveHelpContent(pathname);
  const pageTitle = resolvePageTitleFromPath(pathname);

  const handleOpen = useCallback(() => setOpen(true), [setOpen]);
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  return (
    <>
      <Tooltip title="Ayuda de esta pantalla" enterTouchDelay={0}>
        <span>
          <IconButton
            color="inherit"
            onClick={handleOpen}
            aria-label="Abrir ayuda de esta pantalla"
            aria-haspopup="dialog"
            aria-expanded={open}
            size="medium"
            sx={{
              color: '#00548f',
              '&:hover': {
                backgroundColor: 'rgba(0, 84, 143, 0.08)',
              },
            }}
          >
            <HelpOutlineIcon />
          </IconButton>
        </span>
      </Tooltip>
      <PageHelpDrawer open={open} onClose={handleClose} pageTitle={pageTitle} content={content} />
    </>
  );
};

export default PageHelpButton;
